/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { MentorFeedback } from '@app/app/page-modules/admin/services/mentor-mentee/mentor-mentee-data.service';
import { ContentTemplateDetails } from '@app/app/page-modules/programs/services/templates/templates-data.service';
import {
  EventParticipationStatus,
  EventReminder,
  EventTicketsType,
} from '@app/app/page-modules/resource/store/admin/resource-event-admin.state.model';
import {
  AvailableLanguage,
  CardsLayoutType,
  EditorContent,
  ImageUploadContent,
  LmsPlatformType,
  PlaylistCardShort,
  ResourceAuthorDetails,
  ResourceSubheader,
  ResourceTag,
  ReviewStatusSummary,
} from '..';
import { RequirementsMode, ReviewerDetails } from '../../../page-modules/playlist/models';
import { BrowseSettings } from '../browse/browse-settings.model';
import { ContentStorePlaylistPermissions } from '../content-store/content-store.model';
import { DataFormSummary } from '../data-form/data-form.model';
import { LikeInfoDetails } from '../like-info/like-info.model';
import { SkillsDetails } from '../skills/skills.model';
import { PlaylistPublishedStatus } from './learner-playlist-summary.model';

export interface Playlist {
  /** The playlist UID. */
  _id: string;
  /** The playlist UID. */
  playlistId?: string;
  /** The playlist name. */
  title: string;
  /** The playlist name. */
  header?: string;
  /** The main(top) section of the playlist. */
  mainSection: PlaylistMainSection;
  /** The playlist standard sections. */
  standardSections: PlaylistStandardSection[];
  /** The playlist author details. */
  authorDetails: ResourceAuthorDetails;
  /** Defines the publication settings. */
  publicationSettings?: PlaylistPublicationSettings;
  lastModifiedDate?: string;
  syndicationActivationDate?: string;
  permission: ResourcePermission;
  /** Registration requests waiting for a decision; only set for the people who decide on them. */
  pendingRegistrations?: number | null;
  /** The learner's join state in the context the content was opened from, on v3 organizations. */
  joinState?: JoinStateDetails | null;
  uri: string;
  formattedUri: string;
  syndicated?: boolean;
  thumbnail?: string;
  image?: string;
  syndication?: {
    publisher: {
      organization: {
        name: string;
      };
    };
    license: { masterId?: string; permission?: ContentStorePlaylistPermissions };
    status: SyndicationStatus;
    activatedOn: Date;
    deactivatedOn: Date;
  };
  lastUpdatedOn: string;
  status: PlaylistPublishedStatus;
  adminRights: PlaylistAdminRights[];
  tags: ResourceTag[];
  feedTags?: ResourceTag[];
  categories: ResourceTag[];
  completed: boolean;
  completedOn: string | null;
  likes: LikeInfoDetails;
  favorite: boolean;
  embedded: boolean;
  type: PlaylistType;
  timeRequired?: number;
  costSettings?: CostSettings;
  subHeader: string;
  completedCardsCount: number;
  totalCardsCount: number;
  availableLanguages?: AvailableLanguage[];
  draft: PlaylistDraft;
  dataForms: DataFormSummary[];
  userPlaylistSubmissionSummary: UserPlaylistSubmissionSummary;
  enrollmentStatus?: UserEnrollmentStatus;
  mentorAccessToSubmissions?: boolean;
  mentorsFeedback?: MentorFeedback[];
  projectFormattedUri?: string;
  contentTemplateDetails?: ContentTemplateDetails;
  skills?: SkillsDetails;
  cardsCount?: number;
  browse?: BrowseSettings;

  /** LMS integration fields */
  lmsCourseUrl?: string;
  lmsPlatformType?: LmsPlatformType;
  lmsCourseId?: number;
  moodlePluginCourse?: boolean;
}

/** The main section of the playlist. */
export interface PlaylistMainSection {
  /** The cards included in the main section. */
  cards: PlaylistCardShort[];
  /** Any dynamic content. */
  content: EditorContent[];
  /** The url details of the main image. */
  imageUrl?: ImageUploadContent;
  /** The playlist name. */
  title: string;
  /** Main section cards layout type */
  cardsLayoutType: CardsLayoutType;
  /** The layout type */
  layoutType: PlaylistLayoutType;
  /** The playlist section UID. */
  uid: string;

  /** Frontend specific */
  imageToUpload?: File;
}

/** Defines the playlist section structure. */
export interface PlaylistStandardSection {
  /** The playlist section UID. */
  uid: string;
  /** The section title. */
  title?: string;
  /** The cards included in the section. */
  cards: PlaylistCardShort[];
  /** Section cards layout type */
  cardsLayoutType: CardsLayoutType;
  /** Any dynamic content. */
  content: EditorContent[];
  /** The layout type */
  layoutType?: string;
}

/** Defines default Playlist titles. */
export enum PlaylistDefaultText {
  START_HERE = 'Start here',
  CONTINUE = 'Continue',
  UPCOMING = 'Upcoming',
  YOUR_PLAYLIST = 'Your playlists',
}

/** Defines the playlist section structure. */
export interface FavoritePlaylist {
  /** The favorite playlist section image. */
  image: string;
  /** The favorite playlist id. */
  playlistId: string;
  /** The favorite playlist id. */
  id?: string;
  /** The section title. */
  title: string;
  organizationName: string;
  favorite: boolean;
  completed: boolean;
  uri: string;
  authorDetails?: ResourceAuthorDetails;
  likes?: LikeInfoDetails;
  thumbnail?: string;
  subHeader?: ResourceSubheader[];
  description?: string;

  /** LMS integration fields */
  lmsCourseUrl?: string;
  lmsPlatformType?: LmsPlatformType;
  lmsCourseId?: number;
}

/** Defines the playlist search structure. */
export interface SearchPlaylist {
  description: string;
  subHeader: string;
  id: string;
  thumbnail?: string;
  title: string;
  uri: string;
  syndicated: boolean;
  hasAdminRights: boolean;
  status: PlaylistPublishedStatus;
}

export interface ReviewerDashboardPermission {
  accessForGroups: string[];
}

export interface ResourcePermission {
  canEdit: boolean | undefined;
  canPublish: boolean | undefined;
  canEditGoal?: boolean;
  canEditBadge?: boolean;
  canManageTargeting?: boolean;
  canViewAnalytics: boolean | undefined;
  canDoMemberExports: boolean | undefined;
  canManageCollaboration?: boolean | undefined;
  canClone?: boolean;
  reviewerDashboardPermission?: ReviewerDashboardPermission | undefined;
}

export interface FavoritePlaylistSummary {
  title: string;
  formattedUri: string;
  uri: string;
  playlistId?: string;
  type?: PlaylistType;
  image?: string | null;
  thumbnail?: string | null;
  hasThumbnail?: boolean;
  organizationName?: string;
  totalCardsCount?: number;
  completedCardsCount?: number;
  completed?: boolean;
  syndicated?: boolean;
  deadline?: string | null;
}

export interface LinkablePlaylistSummary {
  header: string;
  subHeader?: string;
  id: string;
  organizationLogo?: string;
  status?: { type: PlaylistPublishedStatus; on: Date; by: string };
  syndicated?: boolean;
  thumbnail?: string;
  formattedUri?: string;
  uri?: string;
}

export interface PlaylistPublicationSettings {
  /** Frontend specific. */
  published?: boolean;
  target?: TargetDetails;
  publishedDate?: Date;
  enrollment?: TargetEnrollmentDetails;
  isShared?: boolean;
  /** V3 organizations only, resolved by the API when none is stored yet. */
  audience?: Audience;
  /** V3 organizations only: "Register first" settings, missing while learners self-enrol. */
  registration?: RegistrationSettings;
  /** V3 organizations only, for admins of content with its own audience. */
  enrolments?: ContentEnrolmentCounts;
}

export type RegistrationApproval =
  | { mode: 'AUTOMATIC' }
  | {
      mode: 'MANUAL';
      /** People who can decide in addition to the content admins. */
      approverUids?: string[];
      decideWithinDays?: number;
    };

export type WaitingListPolicy = { mode: 'AUTO_PROMOTE' } | { mode: 'OFFER_SEAT'; acceptWithinHours: number };

/** `tickets.tickets.time` is the registration window, `tickets.time` the session dates. */
/** Where a learner stands on joining v3 content; `NO_ACCESS` content isn't shown to them. */
export type JoinStateType =
  | 'CAN_ENROL'
  | 'CAN_REGISTER'
  | 'REGISTRATION_NOT_OPEN'
  | 'REGISTRATION_CLOSED'
  | 'FULL'
  | 'CAN_JOIN_WAITING_LIST'
  | 'PENDING_APPROVAL'
  | 'WAITLISTED'
  | 'SEAT_OFFERED'
  | 'INVITED'
  | 'ENROLLED'
  | 'NO_ACCESS';

/**
 * @property seatsLeft - while registration is open on content with limited seats
 * @property waitlistPosition - 1 for the next person to get a seat
 * @property expiresAt - when a seat offer or an invitation expires; absent when it never does
 */
export interface JoinStateDetails {
  state: JoinStateType;
  seatsLeft?: number | null;
  registrationOpensAt?: string | null;
  registrationClosesAt?: string | null;
  waitlistPosition?: number | null;
  expiresAt?: string | null;
}

export interface RegistrationSettings {
  approval: RegistrationApproval;
  tickets: EnrollmentTicketsConfiguration;
  waitingListPolicy?: WaitingListPolicy;
  waitingListLimit?: number;
  dataFormUid?: string;
}

/** `seatsTaken` includes the seats held by pending requests and seat offers. */
export interface ContentEnrolmentCounts {
  enrolled: number;
  seatsTaken: number;
  pendingApprovals: number;
  waitlisted: number;
  seatsOffered: number;
  invited: number;
  declined: number;
  rejected: number;
  expired: number;
  withdrawn: number;
}

export type ContentJoinMethod = 'SELF_ENROL' | 'REGISTER_FIRST' | 'ACCEPT_INVITATION' | 'ENROLLED_ON_INVITATION';

export interface ContentUsagePlaylist {
  uid: string;
  uri: string;
  title: string;
  type: string;
}

/** A playlist v3 content can be opened from, with the settings learners get there. */
export interface ContentUsage {
  playlist: ContentUsagePlaylist;
  /** The playlist the content belongs to: its settings also apply when the content is opened directly. */
  parent: boolean;
  /** The playlist whose settings apply: `playlist` itself, or the one above it it inherits from. */
  settingsFrom?: ContentUsagePlaylist;
  /** Missing when no playlist above has its own settings, so only admins get access. */
  publicationSettings?: PlaylistPublicationSettings;
  /** Missing when nobody can join. */
  joinMethod?: { method: ContentJoinMethod; approval?: 'AUTOMATIC' | 'MANUAL'; seats?: number };
}

/** How an invitation stands for the people managing the audience. */
export type InviteeStatus = 'NOT_SENT' | 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'LEFT';

export interface Invitation {
  uid: string;
  status: InviteeStatus;
  learner: { uid: string; name: string; email: string; imageUrl?: string };
  invitedBy?: string;
  /** Missing while the invitation waits for the content to be published. */
  sentAt?: string;
  /** Missing for invitations that never expire, or that were answered. */
  expiresAt?: string;
  remindedAt?: string;
}

/** `counts` covers every invitation on the content, not just this page. */
export interface InvitationsPage {
  content: Invitation[];
  totalNumberOfElement: number;
  pageSize: number;
  currentPageNumber: number;
  counts: Partial<Record<InviteeStatus, number>>;
}

export type InvitationFailure =
  | 'USER_NOT_FOUND'
  | 'INVALID_EMAIL'
  | 'NOT_ALLOWED_TO_CREATE_ACCOUNT'
  | 'ACCOUNT_NOT_CREATED'
  | 'ALREADY_INVITED'
  | 'ALREADY_JOINED'
  | 'CHANGED';

/** The outcome of inviting one person, as they were asked for: by `userUid` or by `email`. */
export interface InviteeResult {
  userUid?: string;
  email?: string;
  status?: InviteeStatus;
  failure?: InvitationFailure;
  accountCreated?: boolean;
}

export interface InvitationImport {
  invitees: InviteeResult[];
  /** `row` 1 is the first row after the header. */
  invalidRows: { row: number; value?: string; failure: InvitationFailure }[];
}

export type AudienceMode = 'INHERITED' | 'SELECTED' | 'INVITED' | 'EVERYONE' | 'PRIVATE';

export interface AudienceReminder {
  amount: number;
  timeUnit: 'HOURS' | 'DAYS' | 'WEEKS';
}

export type Audience =
  | { mode: 'INHERITED' }
  | { mode: 'SELECTED' }
  | {
      mode: 'INVITED';
      enrolWithoutAcceptance?: boolean;
      expiresAfterDays?: number;
      reminder?: AudienceReminder;
      message?: string;
    }
  | { mode: 'EVERYONE' }
  | { mode: 'PRIVATE' };

export interface TargetDetails {
  groups: PlaylistTargetGroup[];
  users: PlaylistTargetUser[];
  sectors?: string[];
  /** V3 organizations only. */
  programs?: TargetProgram[];
}

export interface TargetProgram {
  programUid: string;
  title: string;
  forceSubscribe: boolean;
}

/** A programme the audience can be targeted to, as listed by `GET /programs`. */
export interface TargetProgramOption {
  id: string;
  title: string;
  subHeader?: string;
  learnersCount?: number;
}

export interface PlaylistTargetSector {
  code: string;
  name: string;
}

export interface PlaylistTargetUserOrGroup {
  _id?: string;
  title: string;
  type: PlaylistTargetUserOrGroupType;
}

export interface PlaylistTargetGroup {
  _id: string;
  name: string;
  type?: string;
  forceSubscribe: boolean;
}

export interface PlaylistTargetUser {
  _id: string;
  name: string;
}

export interface PlaylistTargetUserOrGroupRemove {
  id: string;
  type: PlaylistTargetUserOrGroupType;
}

export interface PlaylistAdminRights {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  permission: ResourcePermission;
  imageUrl: string;
  author: boolean;
  isEnabled: boolean;
  isWorkspaceAdmin: boolean;
}

export interface PlaylistAssociateAdmin {
  uid: string;
  name: string;
  permission: ResourcePermission;
}

export interface PlaylistDraft {
  editor: ResourceAuthorDetails;
  startedOn: Date;
  uri: string;
}

export interface UserPlaylistSubmissionSummary {
  lateSubmissions: boolean;
  requirementsMode: RequirementsMode;
  submissionDeadline: Date;
  submissionMode: UserSubmissionMode;
  submittedOn: Date;
  totalCompleted: number;
  totalRequired: number;
  userReviewSummary: UserReviewSummary;
  allowSubmissionDownload: boolean;
  hasAnyFormSubmitted: boolean;
  submissionsLocked: boolean;
}

export interface UserReviewSummary {
  reviewType: UserReviewType;
  reviewers: ReviewerDetails[];
  status?: ReviewStatusSummary;
  maxSelectableReviewers?: number;
  areReviewersFixed: boolean;
  canSelectNewReviewers: boolean;
}

export interface PlaylistSectionsReorder {
  dragIndex: number;
  dropIndex: number;
}

export type UserReviewType = 'AUTOMATIC' | 'REVIEWER' | 'PEER_REVIEW';

export type UserSubmissionMode = 'PROGRESSIVE' | 'SINGLE';

export type PlaylistTargetUserOrGroupType = 'GROUP' | 'MEMBER';

export type PlaylistType =
  | 'STANDARD'
  | 'PERSONALITY_INDICATOR'
  | 'INSTITUTIONAL_LIBRARY'
  | 'PROJECT'
  | 'LTI_COURSE'
  | 'SCORM'
  | 'PROGRAM'
  | 'COURSE'
  | 'COLLECTION';

export type SyndicationStatus = 'ACTIVE' | 'INACTIVE';

export interface EnrollmentTicketsConfiguration {
  tickets?: EventTicketsDetails;
  time?: EventTime;
  reminders?: EventReminder[];
  participationCode?: string;
  eventLocation?: string;
}

/** Defines the Main section layout types. */
export type PlaylistLayoutType = 'TOP_BANNER' | 'ASIDE_BANNER';

export interface EnrollmentTicketsConfigurationDetails {
  eventLocation?: string;
  tickets?: EventTicketsDetails;
  time?: EventTime;
  reminders?: EventReminder[];
}

export interface EventTicketsDetails {
  availability?: EventTicketsType;
  quantity?: number;
  time?: EventTime;
  remainingTickets?: number;
  waitingList: boolean;
}

export interface EventTime {
  start: string; // Using string type for date format "yyyy-MM-dd HH:mm"
  end?: string;
  timeZone: string;
}

export interface TargetEnrollmentDetails {
  actionTrigger?: ActionTriggerDetails;
  mode: EnrollmentMode;
  groups?: PlaylistTargetGroup[];
}

export interface ActionTriggerDetails {
  mode: EnrollmentActionType;
  dataFormUid?: string;
  configuration?: EnrollmentTicketsConfigurationDetails;
}

export enum EnrollmentActionType {
  ENGAGEMENT = 'ENGAGEMENT',
  DATA_FORM = 'DATA_FORM',
  REQUEST_FOR_APPROVAL = 'REQUEST_FOR_APPROVAL',
  PAYMENT = 'PAYMENT',
  TICKET = 'TICKET',
  OPEN = 'OPEN',
  CUSTOM_GATE = 'CUSTOM_GATE',
  ADMIN_ENROLLED = 'ADMIN_ENROLLED',
}

export interface EnrollmentSettingsRequest {
  enrollmentMode: EnrollmentMode;
  enrollmentAction: EnrollmentActionType;
  dataFormUid?: string;
  configuration?: EnrollmentTicketsConfiguration;
  groups?: string[];
}

export interface EnrollmentSettingsUpdateRequest extends EnrollmentSettingsRequest {
  playlistId: string;
}

export enum EnrollmentMode {
  ANYONE = 'ANYONE',
  GROUP = 'GROUP',
  INVITE = 'INVITE',
  PRIVATE = 'PRIVATE',
  PUBLIC = 'PUBLIC',
}

export interface UserEnrollmentStatus {
  participationCode?: string;
  participationStatus: EventParticipationStatus;
  participationStatusOn?: string;
  waitingListSpot?: number;
  groups?: PlaylistTargetGroup[];
}

export interface CostSettings {
  amount: number;
  currency: Currency;
}

export interface CostSettingsRequest {
  amount: number;
  currency: Currency;
}

export enum Currency {
  DOLLAR = 'DOLLAR',
  POUND = 'POUND',
  EURO = 'EURO',
}

export const CurrencySymbols = {
  [Currency.DOLLAR]: '$',
  [Currency.POUND]: '£',
  [Currency.EURO]: '€',
} as const;
